// allowance-status.component.ts
import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AgGridModule } from 'ag-grid-angular';
import { ColDef, GridApi, GridReadyEvent, ProcessCellForExportParams } from 'ag-grid-community';
import { AllowanceStatusRow } from '../../models/ratings.type';
import { ServerGridCore } from '../../models/BaseServerGrid';
import { RatingsService } from '../../shared/services/ratings/ratings.service';
import { compareDates, dateFilterComparator, formatDMY } from '../../shared/utils/date-helpers';
import { UserService } from '../../shared/services/user.service';
import { Router } from '@angular/router';
import { BookmarksComponent } from "../bookmarks/bookmarks.component";
import { MatSidenavModule } from "@angular/material/sidenav";
import { AppraisalDataService } from "../../shared/services/appraisal-data.service";

@Component({
  selector: 'app-allowance-status',
  standalone: true,
  imports: [CommonModule, AgGridModule, MatSidenavModule, BookmarksComponent],
  templateUrl: './allowance-status.component.html',
  styleUrls: ['./allowance-status.component.scss']
})
export class AllowanceStatusComponent extends ServerGridCore<AllowanceStatusRow> {
  bookmarkGrid: string = "allowanceStatus";
  override api!: GridApi;
  override columnDefs: ColDef[] = [
    { field: 'companyName', headerName: 'Company', filter: 'agTextColumnFilter', hide: true, minWidth: 200 },
    { field: 'divisionName', headerName: 'Division', filter: 'agTextColumnFilter', sortable: true, sort: 'asc' },
    { field: 'clientReference', headerName: 'Client Ref', filter: 'agTextColumnFilter' },
    { field: 'addressPrimary', headerName: 'Address', filter: 'agTextColumnFilter', minWidth: 220 },
    { field: 'cityName', headerName: 'City', filter: 'agTextColumnFilter' },
    { field: 'countryName', headerName: 'Country', filter: 'agTextColumnFilter' },
    {
      field: 'liabilityStartDate',
      headerName: 'Liability Start',
      sortable: true,
      // filter: 'agDateColumnFilter',
      valueFormatter: p => formatDMY(p.value),
      comparator: compareDates,
      filterParams: {
        comparator: dateFilterComparator,
        browserDatePicker: true,              // show picker
        buttons: ['reset', 'apply'],
        debounceMs: 0
      }
    },
    {
      field: 'liabilityEndDate', 
      headerName: 'Liability End',
      sortable: true,
      // filter: 'agDateColumnFilter',
      valueFormatter: p => formatDMY(p.value),
      comparator: compareDates,
      filterParams: {
        comparator: dateFilterComparator,
        browserDatePicker: true,              // show picker
        buttons: ['reset', 'apply'],
        debounceMs: 0
      }
    },
    { field: 'allowanceType', headerName: 'Allowance Type', filter: 'agTextColumnFilter' },
    { field: 'reliefPercent', headerName: 'Relief %', filter: 'agNumberColumnFilter' },
    { field: 'allowanceStatus', headerName: 'Allowance Status', filter: 'agTextColumnFilter' },
    { field: 'revaluationYear', headerName: 'Revaluation Period', hide: true,  filter: 'agTextColumnFilter' }
  ];

  excelStyles = [
    { id: 'header', font: { bold: true } },
    { id: 'date', dataType: 'dateTime', numberFormat: { format: 'dd-mm-yyyy' } },
    { id: 'percent2', numberFormat: { format: '0.00%' } },
    { id: 'int', numberFormat: { format: '#,##0' } }
  ];

  userData: any;
  constructor(private apiSvc: RatingsService, private userService: UserService, public router: Router,
    private appraisalDataService: AppraisalDataService
  ) { super(); }

  async ngOnInit() {
    this.userService.userData$.subscribe((data) => {
      this.userData = data;
      if (!this.userData.roles.includes('Ratings')) {
        this.router.navigate(['/404']);
      }
    });
  }

  onGridReady(e: GridReadyEvent) {
    this.api = e.api;
    this.api.setGridOption('serverSideDatasource', this.makeDatasource());
  }

  // ---- Export button handler (Excel with CSV fallback)
  onExport() {
    if (!this.api) return;

    const columnKeys = this.api.getAllDisplayedColumns().map(c => c.getColId());
    const today = new Date();
    const fileName = `allowance_status_${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}.xlsx`;
    const hasExcel = typeof (this.api as any).exportDataAsExcel === 'function';

    const processCell = (p: ProcessCellForExportParams) => {
      const f = p.column.getColDef().field;
      if (!f) return p.value;

      // Normalize date fields to ISO-like for Excel/CSV
      if ((f === 'liabilityStartDate' || f === 'liabilityEndDate') && p.value) {
        const d = new Date(p.value);
        if (!isNaN(d.getTime())) {
          return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        }
      }
      return p.value;
    };

    if (hasExcel) {
      (this.api as any).exportDataAsExcel({
        fileName,
        sheetName: 'Allowance Status',
        columnKeys,
        processCellCallback: processCell,
        excelStyles: this.excelStyles,
        // Note: With Server-Side Row Model, this exports rows currently loaded on the client.
        // Implement server-side Excel export if you need true "all rows".
      });
    } else {
      this.api.exportDataAsCsv({
        fileName: fileName.replace('.xlsx', '.csv'),
        columnKeys,
        processCellCallback: processCell
      });
    }
  }

  override project(): string[] {
    const m: Record<string, string> = {
      id: 'ID', divisionName: 'DivisionName', clientReference: 'ClientReference', addressPrimary: 'AddressPrimary', cityName: 'CityName',
      countryName: 'CountryName', liabilityStartDate: 'LiabilityStartDate', liabilityEndDate: 'LiabilityEndDate', allowanceType: 'AllowanceType',
      reliefPercent: 'ReliefPercent', allowanceStatus: 'AllowanceStatus', revaluationID: 'RevaluationID', revaluationYear: 'RevaluationYear',
      companyName: 'CompanyName'
    };
    const cols = this.api?.getAllDisplayedColumns() || [];
    return cols.map(c => m[c.getColDef().field!] || c.getColDef().field!);
  }

  override query(body: any) { return this.apiSvc.allowanceStatusQuery(body); }

  isBookmarksPanelOpen: boolean = false;
  ontoggleBookmarkSidenav() {
    let state = this.api.getState();
    this.appraisalDataService.setGridStateData(state);
    this.isBookmarksPanelOpen = !this.isBookmarksPanelOpen;
  }
  onStateUpdated(): void {
    let state = this.api.getState();
    this.appraisalDataService.setGridStateData(state);
  }

  loadBookmark() {
    let state = this.appraisalDataService.getGridStateData();
    this.api.setState(state);
  }
}
